import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  FEATURE_FOCUS_STALE_MS,
  FEATURE_STALE_MS,
  clearFeatureCache,
  matchesInvalidation,
  readCachedFeature,
  writeFeatureCache,
  type InvalidationDetail,
} from '@/lib/feature-access-cache';

interface UseFeatureAccessReturn {
  hasAccess: boolean;
  loading: boolean;
}

// Cache for feature access results to avoid repeated RPC calls.
const featureAccessCache = new Map<string, boolean>();
// When each in-memory answer was confirmed. Separate from the boolean so a
// value can be painted while still being old enough to re-check.
const featureCheckedAt = new Map<string, number>();
// Last time we STARTED a re-check, so focus + the interval + a remount in the
// same few seconds collapse onto one RPC.
const lastAttemptAt = new Map<string, number>();

// In-flight RPCs, keyed the same way. THIS is the fix for the call volume:
// the resolved cache above is only written when a call RETURNS, so every hook
// that mounted in the same tick used to miss it and fire its own duplicate.
// `useFeatureAccess('tasks')` alone has four call sites. Parking the promise
// here means the first caller issues the RPC and the rest await that same one.
// See src/lib/feature-access-cache.ts for the full measurement.
const inFlight = new Map<string, Promise<boolean | null>>();

// Bumped on invalidate so an in-flight response from before the clear cannot
// write itself back over the answer the next request is about to learn.
let clearAllEpoch = 0;
const keyEpoch = new Map<string, number>();

// Mounted gates. The interval re-checks these and nothing else, so a feature
// nobody is showing does not keep a query alive.
const subscribers = new Map<string, { companyId: string; featureName: string; count: number }>();

// Custom event mounted hooks listen on to know they should re-read.
// Fired by invalidateFeatureAccess() when a saved answer must be dropped, and
// by a background re-check when a value actually changed. Same event so every
// gate already listening picks the new answer up without a second channel.
const INVALIDATE_EVENT = 'feature_access_invalidated';

// Spread the sitting-open re-check so every open tab does not hit the
// database on the same tick after a deploy. Reload and focus do not wait
// for this; they re-check as soon as the saved answer is a minute old.
const INTERVAL_JITTER_MS = Math.floor(Math.random() * FEATURE_FOCUS_STALE_MS);

let listenersBound = false;
let watchTimer: number | undefined;

/** localStorage where it exists — absent under SSR and in locked-down mode. */
function persistentStore() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function cacheKey(companyId: string, featureName: string) {
  return `${companyId}-${featureName}`;
}

function subscriptionKey(companyId: string, featureName: string) {
  return `${companyId}\n${featureName}`;
}

function bumpKeyEpoch(key: string) {
  keyEpoch.set(key, (keyEpoch.get(key) ?? 0) + 1);
}

function forgetRuntime(key: string) {
  featureAccessCache.delete(key);
  featureCheckedAt.delete(key);
  lastAttemptAt.delete(key);
  inFlight.delete(key);
  bumpKeyEpoch(key);
}

function publishFeatureChange(companyId?: string, featureName?: string) {
  try {
    window.dispatchEvent(new CustomEvent(INVALIDATE_EVENT, { detail: { companyId, featureName } }));
  } catch {
    // SSR or detached window — ignore
  }
}

/**
 * Drop cached feature-access results so the next read re-runs the RPC.
 * Pass nothing to clear everything; pass a company_id + feature key to
 * clear just that one. Triggers a re-render in any mounted useFeatureAccess
 * via the INVALIDATE_EVENT.
 */
export function invalidateFeatureAccess(companyId?: string, featureName?: string) {
  if (companyId && featureName) {
    forgetRuntime(cacheKey(companyId, featureName));
  } else if (companyId) {
    const prefix = `${companyId}-`;
    const keys = new Set<string>([
      ...featureAccessCache.keys(),
      ...featureCheckedAt.keys(),
      ...lastAttemptAt.keys(),
      ...inFlight.keys(),
    ]);
    for (const key of keys) {
      if (key.startsWith(prefix)) forgetRuntime(key);
    }
  } else {
    featureAccessCache.clear();
    featureCheckedAt.clear();
    lastAttemptAt.clear();
    inFlight.clear();
    keyEpoch.clear();
    clearAllEpoch += 1;
  }
  // Clear the persisted tier too, or a reload would resurrect what was just
  // invalidated and the override would look like it never applied.
  clearFeatureCache(persistentStore(), companyId ?? null);
  publishFeatureChange(companyId, featureName);
}

/**
 * Resolve one feature for one company, hitting the network at most once per
 * (company, feature) across every concurrent caller.
 *
 * Returns null on any failure and does NOT write the cache. A first load
 * treats null as "off" (fail closed, same as before). A background re-check
 * treats null as "keep whatever we already showed". A network error must
 * never overwrite a saved answer with false.
 */
function requestFeature(companyId: string, featureName: string): Promise<boolean | null> {
  const key = cacheKey(companyId, featureName);

  const pending = inFlight.get(key);
  if (pending) return pending;

  const capturedClearAll = clearAllEpoch;
  const capturedKeyEpoch = keyEpoch.get(key) ?? 0;

  const request = (async () => {
    try {
      const { data, error } = await supabase.rpc('has_feature_access', {
        _company_id: companyId,
        _feature_name: featureName,
      });
      // Invalidated while we were in flight. Drop the response so a slow
      // answer cannot resurrect what invalidateFeatureAccess just cleared,
      // and so it cannot arrive after a newer request has written.
      if (clearAllEpoch !== capturedClearAll || (keyEpoch.get(key) ?? 0) !== capturedKeyEpoch) {
        return null;
      }
      if (error || typeof data !== 'boolean') {
        if (error || data === null) {
          console.error(`[FEATURE_ACCESS] "${featureName}" error:`, error);
        }
        return null;
      }
      const now = Date.now();
      featureAccessCache.set(key, data);
      featureCheckedAt.set(key, now);
      // Merge into the persisted map so a reload starts warm. Only THIS
      // feature's confirmation clock moves.
      writeFeatureCache(persistentStore(), companyId, { [featureName]: data }, now);
      return data;
    } catch (error) {
      console.error(`[FEATURE_ACCESS] "${featureName}" exception:`, error);
      return null;
    } finally {
      // Always release, success or not — leaving a rejected promise parked here
      // would pin a permanent answer for the life of the tab. Only drop the
      // entry if it is still THIS request: invalidate + a newer fetch can
      // have replaced it while we were in flight.
      if (inFlight.get(key) === request) inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}

/**
 * How old the saved answer is. Null means we have nothing saved (the cold
 * path owns the first fetch). Infinity means we have a value but no
 * confirmation clock, which is stale.
 */
function ageOf(companyId: string, featureName: string, now: number): number | null {
  const key = cacheKey(companyId, featureName);
  const memAt = featureCheckedAt.get(key);
  if (memAt !== undefined) return Math.max(0, now - memAt);

  const cached = readCachedFeature(persistentStore(), companyId, featureName);
  const inMemory = featureAccessCache.has(key);
  if (!cached && !inMemory) return null;
  if (cached?.confirmedAt != null && Number.isFinite(cached.confirmedAt)) {
    return Math.max(0, now - cached.confirmedAt);
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * Re-check one saved answer if it is old enough and we did not just try.
 * Paints nothing itself. On a real change, fires the same invalidation event
 * the Company Features screen uses so every mounted gate re-reads.
 * On failure, leaves the saved answer untouched.
 */
function revalidateFeature(companyId: string, featureName: string, minAgeMs: number): Promise<void> {
  if (!companyId || !featureName) return Promise.resolve();
  const now = Date.now();
  const age = ageOf(companyId, featureName, now);
  if (age === null || age < minAgeMs) return Promise.resolve();

  const key = cacheKey(companyId, featureName);
  const last = lastAttemptAt.get(key);
  if (last !== undefined && now - last < FEATURE_FOCUS_STALE_MS) return Promise.resolve();
  lastAttemptAt.set(key, now);

  const previous = featureAccessCache.has(key) ? featureAccessCache.get(key) : undefined;
  return requestFeature(companyId, featureName).then((value) => {
    if (value === null || value === previous) return;
    publishFeatureChange(companyId, featureName);
  });
}

function revalidateSubscribers(minAgeMs: number) {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  for (const sub of subscribers.values()) {
    void revalidateFeature(sub.companyId, sub.featureName, minAgeMs);
  }
}

function syncWatchTimer() {
  if (typeof window === 'undefined') return;
  if (subscribers.size === 0) {
    if (watchTimer !== undefined) {
      window.clearTimeout(watchTimer);
      watchTimer = undefined;
    }
    return;
  }
  if (watchTimer !== undefined) return;
  watchTimer = window.setTimeout(() => {
    watchTimer = undefined;
    revalidateSubscribers(FEATURE_STALE_MS);
    syncWatchTimer();
  }, FEATURE_STALE_MS + INTERVAL_JITTER_MS);
}

function ensureWatchers() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  const onForeground = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void revalidateSubscribers(FEATURE_FOCUS_STALE_MS);
  };
  window.addEventListener('focus', onForeground);
  document.addEventListener('visibilitychange', onForeground);
}

function retainFeatureSubscription(companyId: string | null | undefined, featureName: string) {
  if (!companyId || !featureName) return () => {};
  const key = subscriptionKey(companyId, featureName);
  const existing = subscribers.get(key);
  if (existing) existing.count += 1;
  else subscribers.set(key, { companyId, featureName, count: 1 });
  ensureWatchers();
  syncWatchTimer();
  return () => {
    const cur = subscribers.get(key);
    if (!cur) return;
    cur.count -= 1;
    if (cur.count <= 0) subscribers.delete(key);
    syncWatchTimer();
  };
}

function adoptCached(companyId: string, featureName: string, value: boolean, confirmedAt: number | null) {
  const key = cacheKey(companyId, featureName);
  featureAccessCache.set(key, value);
  if (confirmedAt !== null && Number.isFinite(confirmedAt)) featureCheckedAt.set(key, confirmedAt);
}

export function useFeatureAccess(featureName: string): UseFeatureAccessReturn {
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  // Seed from the cache on the first render so a reload paints the last
  // answer instead of a frame of "loading" before the effect runs. The
  // effect still re-checks when that answer is stale.
  const [hasAccess, setHasAccess] = useState(() => {
    if (!companyId || !featureName) return false;
    const key = cacheKey(companyId, featureName);
    if (featureAccessCache.has(key)) return featureAccessCache.get(key)!;
    return readCachedFeature(persistentStore(), companyId, featureName)?.value ?? false;
  });
  const [loading, setLoading] = useState(() => {
    if (!companyId || !featureName) return !companyId;
    const key = cacheKey(companyId, featureName);
    if (featureAccessCache.has(key)) return false;
    return readCachedFeature(persistentStore(), companyId, featureName) === null;
  });
  // Bumped by the invalidate event to force the useEffect to re-fire.
  const [invalidationTick, setInvalidationTick] = useState(0);

  useEffect(() => {
    return retainFeatureSubscription(companyId, featureName);
  }, [companyId, featureName]);

  useEffect(() => {
    let cancelled = false;

    if (!companyId) {
      setLoading(false);
      setHasAccess(false);
      return;
    }

    const key = cacheKey(companyId, featureName);
    const applyCached = (value: boolean) => {
      setHasAccess(value);
      setLoading(false);
    };

    // Tier 1: resolved this session. Still re-check in the background once
    // the confirmation is older than a minute (reload / remount) so a tab
    // that has been open does not keep the first answer forever.
    if (featureAccessCache.has(key)) {
      applyCached(featureAccessCache.get(key)!);
      if (featureName) void revalidateFeature(companyId, featureName, FEATURE_FOCUS_STALE_MS);
      return () => {
        cancelled = true;
      };
    }

    // Tier 2: persisted from a previous session. Paint it immediately, then
    // re-check unless it was confirmed in the last minute. A legacy entry
    // has no per-feature clock and is always re-checked. That is the case
    // that used to survive a reload for up to six hours.
    const persisted = readCachedFeature(persistentStore(), companyId, featureName);
    if (persisted) {
      adoptCached(companyId, featureName, persisted.value, persisted.confirmedAt);
      applyCached(persisted.value);
      if (featureName) void revalidateFeature(companyId, featureName, FEATURE_FOCUS_STALE_MS);
      return () => {
        cancelled = true;
      };
    }

    // Tier 3: nothing saved. Ask the database, deduped against every other
    // mounted caller. Failure here still fails closed: we have no last answer
    // to keep.
    setLoading(true);
    requestFeature(companyId, featureName).then((value) => {
      if (cancelled) return;
      setHasAccess(value === true);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [companyId, featureName, invalidationTick]);

  // Listen for invalidation events. Only bump for events that target this
  // company + feature (or that target "all"); ignore unrelated invalidations
  // so other hooks don't pay an unnecessary re-fetch. A background re-check
  // fires the same event when the answer changed, and the effect above then
  // reads the cache it just wrote instead of starting another RPC.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as InvalidationDetail | undefined;
      if (matchesInvalidation(detail, companyId, featureName)) {
        setInvalidationTick((t) => t + 1);
      }
    };
    window.addEventListener(INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(INVALIDATE_EVENT, handler);
  }, [companyId, featureName]);

  return { hasAccess, loading };
}
